import React, { FC, useCallback } from "react";
import HelpButton from "components/HelpButton";
import PanelBackButton from "containers/RightPanel/components/PanelBackButton";
import {
  Content,
  ContentWrapper,
  DrawerFooter,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import AddonsList from "../../../../components/AddonsList/AddonsList";
import Button from "../../../../components/Button/Button";
import PriceBlock from "../../../../components/PriceBlock/PriceBlock";
import { getAddonsCost } from "../../../../components/TotalCostHint/totalCostHelper";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  getAddonsMethods,
  getConsolidationAddonsMethods,
} from "../../../../reduxFolder/reducers/services";
import { updateExpectedItem } from "../../../../reduxFolder/reducers/storage";
import {
  servicesSelectAddons,
  servicesSelectConsolidationAddons,
  servicesSelectLoading,
} from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  Paragraph,
  PriceWrapper,
} from "../../../../styles/serviceRequestStyles";
import {
  AdditionalTitle,
  AddonsForm,
  Spinner,
  Title,
} from "../../../Storage/components/PackOptions/PackOptions.styles";
import { expectedPackOptionsFormik } from "./expectedPackOptionsHelper";

interface ExpectedPackOptionsProps {
  addons: any;
  panelData: any;
  openRightPanel: any;
  getAddonsMethods: () => Promise<any>;
  getConsolidationAddonsMethods: () => Promise<any>;
  consolidationAddons: any;
  isLoading: boolean;
  updateExpectedItem: (
    id: any,
    data: { expected_shipment: any },
  ) => Promise<any>;
}

const ExpectedPackOptions: FC<ExpectedPackOptionsProps> = React.memo(
  ({
    addons,
    panelData,
    openRightPanel,
    getAddonsMethods,
    getConsolidationAddonsMethods,
    consolidationAddons,
    isLoading,
    updateExpectedItem,
  }) => {
    const { t } = useTranslation("common");
    const expectedAddons = addons.map((addon: any) =>
      addon.set("id", addon.get("code")),
    );
    const { values, isSubmitting, setFieldValue, handleSubmit } = useFormik(
      expectedPackOptionsFormik(
        t,
        openRightPanel,
        panelData,
        updateExpectedItem,
      ),
    );

    useFetchOnceEffect(!addons.size, getAddonsMethods);
    useFetchOnceEffect(
      !consolidationAddons.size,
      getConsolidationAddonsMethods,
    );

    const handleCancel = useCallback(() => {
      openRightPanel(
        "EXPECTED_ITEM",
        panelData.merge(
          Map({
            isEditForm: true,
            selectedExpectedItem: panelData,
          }),
        ),
      );
    }, [openRightPanel, panelData]);

    const id = panelData.get("id");
    const items = List([Map({ addons: values[id] })]);

    const handleSelect = useCallback(
      (addon: any) => () => {
        const addonId = addon.get("id");
        const hasAddon = values[id].some((x: string) => x === addonId);
        const value = hasAddon
          ? [...values[id].filter((item: string) => item !== addonId)]
          : [...values[id], addon.get("id")];
        setFieldValue(id, value);
      },
      [id, values, setFieldValue],
    );

    const cost = getAddonsCost(
      List([Map({ addons: values[id] })]),
      expectedAddons,
    );

    const handleHelpClick = useCallback(
      () =>
        openRightPanel(
          "HELP_PANEL",
          panelData
            .set("preventReLoading", true)
            .set("returnPanelType", "EXPECTED_PACK_OPTIONS")
            .set("returnPanelData", panelData)
            .set(
              "headerTitle",
              `${t("serviceRequests.packOptions.title")}: FAQ`,
            )
            .set("showHelpersCode", "single_options"),
        ),
      [panelData, openRightPanel, t],
    );

    return (
      <ContentWrapper>
        <Heading $isBordered>
          <PanelBackButton onClick={handleCancel} />
          <Title>{t("serviceRequests.packOptions.title")}</Title>
        </Heading>
        <Content>
          <AddonsForm onSubmit={handleSubmit}>
            <Spinner isActive={isLoading} />
            <div>
              <Paragraph>
                {t(`serviceRequests.packOptions.paragraph`)}
                <HelpButton onClick={handleHelpClick} />
              </Paragraph>
              <AdditionalTitle>
                {t(`serviceRequests.packOptions.heading`)}:
              </AdditionalTitle>
              <AddonsList
                addons={expectedAddons}
                items={items}
                values={values}
                readOnly={false}
                handleSelect={handleSelect}
              />
              <PriceWrapper>
                <PriceBlock
                  price={cost}
                  title={t(`serviceRequests.packOptions.costHeading`)}
                />
              </PriceWrapper>
            </div>
            <DrawerFooter>
              <Button onClick={handleCancel}>{t("common.cancel")}</Button>
              <Button
                type={ButtonType.Submit}
                color={ButtonColor.Primary}
                variant={ButtonVariant.Filled}
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {t(`serviceRequests.packOptions.submit`)}
              </Button>
            </DrawerFooter>
          </AddonsForm>
        </Content>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    addons: servicesSelectAddons,
    isLoading: servicesSelectLoading,
    consolidationAddons: servicesSelectConsolidationAddons,
  }),
  {
    updateExpectedItem,
    openRightPanel,
    getAddonsMethods,
    getConsolidationAddonsMethods,
  },
);

export default withConnect(ExpectedPackOptions);
